/**
 * 한국어에서 `토씨` 또는 조사는 체언 뒤에 붙어 문법에 의미를 더하는 품사이다.
 * 조사는 명사에 격을 부여하는 접사이기도 하다. 다른 품사와는 다르게 띄어쓰기할 때 항상 앞 낱말에 붙여 쓴다.
 * 격을 부여하는 양상에 따라 나뉘는 데, 이 모듈은 동적으로 변하는 단어에 대해 여러 종류의 조사를 사용할 때 유용하다.
 */
interface JosaMap {
  목적격: '을/를';
  행위주격: '이/가';
  동반격: '과/와';
  보조사: '은/는';
  부사격: '으로/로';
}

const readableLetter = /[a-z|ㄱ-ㅎ|가-힣|0-9]/i;
const koreanSyllable = /[가-힣]/;
// from: https://github.com/coxcore/postposition/blob/master/src/postposition.js
const pronounceAsJongsung = new RegExp(
  `(?:${[
    '[ㄱ-ㄷㅁ-ㅎ036]',
    '^[mn]',
    '\\S[mn]e?',
    '\\S(?:[aeiom]|lu)b',
    '(?:u|\\S[aei]|[^o]o)p',
    '(?:^i|[^auh]i|\\Su|[^ei][ae]|[^oi]o)t',
    '(?:\\S[iou]|[^e][ae])c?k',
    '\\S[aeiou](?:c|ng)',
    'foot|go+d|b[ai]g|private',
    '^(?:app|kor)',
  ].join('|')})$`,
  'i'
);
const pronounceAsRieulJongsung = new RegExp(
  `(?:${['[178ㄹ]', '^[lr]', '^\\Sr', '\\Sle?'].join('|')})$`,
  'i'
);

/**
 * 종성을 갖는 문자열인지 확인
 *
 * @param targetStr 확인할 문자열
 * @return {boolean} 종성에 붙는 조사가 필요한 경우 true, 그렇지 않은 경우 false를 반환
 * @see http://www.unicode.org/charts/PDF/UAC00.pdf
 * @see https://www.3rabbitz.com/blog_ko/44ae93a579d33b9c
 */
function hasJongsung(targetStr: string, format: JosaMap[keyof JosaMap]) {
  const lastChar = targetStr[targetStr.length - 1];

  if (koreanSyllable.test(lastChar)) {
    const charCode = lastChar.charCodeAt(0);

    const jongsungCode = (charCode - 0xac00) % 28;

    // 종성이 있는 경우를 체크하고, 조사 '로'일 때 종성이 'ㄹ'인지 여부 추가 체크
    return !(format === '으로/로' && jongsungCode === 8) && jongsungCode > 0;
  }
  return (
    (format !== '으로/로' && pronounceAsRieulJongsung.test(targetStr)) ||
    pronounceAsJongsung.test(targetStr)
  );
}

/**
 * 문자열에 적절한 조사를 붙여 반환합니다.
 * @example
 * const str = josa('예시', '으로/로') // '예시로'
 */
function josa(targetStr: string, format: JosaMap[keyof JosaMap]) {
  return `${targetStr}${josa.only(targetStr.replace(/['"]/g, ''), format)}`;
}

/**
 * 문자열에 붙는 적절한 조사만을 반환합니다.
 * @example
 * const str = josa.only('예시', '으로/로') // '로'
 */
josa.only = (targetStr: string, format: JosaMap[keyof JosaMap]) => {
  if (!readableLetter.test(targetStr[targetStr.length - 1])) {
    return format !== '으로/로' ? format : '(으)로';
  }

  const [a, b] = format.split('/');

  return hasJongsung(targetStr, format) ? a : b;
};

export default josa;
